import type { AxiosInstance, AxiosResponse } from 'axios';
import { useAppOverlayStore } from '@/stores/appOverlay';
import { useToastStore } from '@/stores/toast';

// Cloudflare's managed challenge guards the whole app domain with a ~45min
// passage. When clearance (the cf_clearance cookie) expires, background
// XHR/fetch/Inertia requests are intercepted and returned a challenge
// interstitial instead of JSON — which XHR cannot render, so the app silently
// stops working until a full page reload re-issues clearance. We detect those
// responses and recover with a single graceful reload.

const RECOVER_KEY = 'cf_challenge_last_recover';
// If we just reloaded and got challenged again, the challenge can't pass
// silently — don't hard-loop; ask the user to refresh instead.
const RELOAD_GUARD_MS = 15_000;
const CHALLENGE_MARKERS = ['cf-chl', '_cf_chl_opt', 'challenges.cloudflare.com'];

function header(headers: unknown, name: string): string | undefined {
    if (headers && typeof headers === 'object') {
        const value = (headers as Record<string, unknown>)[name];
        if (typeof value === 'string') {
            return value;
        }
    }

    return undefined;
}

/**
 * Detect whether a response is a Cloudflare challenge interstitial rather than
 * the JSON/Inertia payload the caller expected.
 */
export function isChallengeResponse(
    response: Pick<AxiosResponse, 'status' | 'headers' | 'data'>,
): boolean {
    if (header(response.headers, 'cf-mitigated') === 'challenge') {
        return true;
    }

    if (![403, 429, 503].includes(response.status)) {
        return false;
    }

    const contentType = header(response.headers, 'content-type') ?? '';
    if (!contentType.includes('text/html')) {
        return false;
    }

    const body = typeof response.data === 'string' ? response.data : '';

    return CHALLENGE_MARKERS.some((marker) => body.includes(marker));
}

/**
 * Recover from an expired challenge. Only a top-level navigation can re-issue
 * clearance, so recovery is always a full reload — guarded so a challenge that
 * cannot pass silently never loops.
 */
export function recoverFromChallenge(): void {
    const last = Number(sessionStorage.getItem(RECOVER_KEY) ?? 0);
    const now = Date.now();

    if (last && now - last < RELOAD_GUARD_MS) {
        useToastStore().error(
            'Your session needs to be refreshed. Please reload the page to continue.',
            0,
        );

        return;
    }

    sessionStorage.setItem(RECOVER_KEY, String(now));
    useAppOverlayStore().show();
    useToastStore().info('Reconnecting…');
    window.location.reload();
}

/**
 * Install a response interceptor that turns challenge responses (on both the
 * success and rejection paths) into a graceful recovery, then lets the original
 * outcome propagate so callers still stop cleanly.
 */
export function installChallengeInterceptor(instance: AxiosInstance): void {
    instance.interceptors.response.use(
        (response) => {
            if (isChallengeResponse(response)) {
                recoverFromChallenge();
            }

            return response;
        },
        (error: unknown) => {
            const response = (error as { response?: AxiosResponse }).response;
            if (response && isChallengeResponse(response)) {
                recoverFromChallenge();
            }

            throw error;
        },
    );
}
